import * as React from "react";
import { Button, message, TablePaginationConfig } from "antd";
import { ColumnProps } from "antd/lib/table";
import { FormatData } from "utils/FormatData";
import { dayjs } from "Config/dayjs";
import { ButtonsRow } from "Components/Form/ButtonsRow";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { FolderTypeIcon } from "Pages/Project/Documents/Components/FolderTypeIcon";
import { FileTypeIcon } from "Pages/Project/Documents/Components/FileTypeIcon";
import { UploadFile } from "antd/lib/upload/interface";
import { DocumentsBreadcrumbs } from "Pages/Project/Documents/Components/DocumentsBreadcrumbs";
import { BreadcrumbsStore } from "Pages/Project/Documents/Stores/BreadcrumbsStore";
import { Table, TableProps } from "Components/General/Table";

interface ProjectDocumentsTableProps extends WithTranslation {
  onExit: () => void;
  onShowSearch?: () => void;
  hideSearch?: boolean;
  handleAddAttachments: (c: Array<API_FileDataEntity>) => void | Promise<void>;
  initialValue?: API_FileDataEntity[] | UploadFile[];
  minimumAccessLevel?: API_DOCUMENTFILTER_MINIMUMACCESSLEVEL;
  isRadioSelection?: boolean;
  startingFolderId?: number;
  allowedFileTypes?: string[];
  fileType?: string | string[];
  fromXKTViewer?: boolean;
}

export interface ProjectDocumentTableItem extends API_FileDataEntity {}

interface ProjectDocumentsTableState {
  data: Array<ProjectDocumentTableItem>;
  selectedRowKeys: number[];
  loading: boolean;
  attachLoading: boolean;
  pagination: TablePaginationConfig;
}

class ProjectFilesColumn extends React.Component<ColumnProps<ProjectDocumentTableItem>> {}

export const ProjectDocumentsTable = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<ProjectDocumentsTableProps, ProjectDocumentsTableState> {
      private selectedFiles: Map<number, API_FileDataEntity>;
      breadcrumbsStore: BreadcrumbsStore;
      constructor(props: ProjectDocumentsTableProps) {
        super(props);
        this.state = {
          data: null,
          selectedRowKeys: [],
          loading: false,
          attachLoading: false,
          pagination: { locale: { items_per_page: `/ ${this.props.t("page")}` } },
        };
        this.breadcrumbsStore = new BreadcrumbsStore();
        this.selectedFiles = new Map();
      }

      componentDidMount() {
        const { startingFolderId } = this.props;
        (async () => {
          await this.loadData(startingFolderId);
        })();
        if (this.props.initialValue) {
          // this.setInitialData();
        }
        this.breadcrumbsStore.init(startingFolderId);
      }

      setInitialData = () => {
        const checkedFiles = [];
        this.props.initialValue.forEach((file) => {
          checkedFiles.push(file.id);
          this.selectedFiles.set(file.id, file);
        });
        this.setState({ selectedRowKeys: checkedFiles });
      };

      onSelectChange = (selectedRowKeys: number[], selectedRows) => {
        selectedRows.forEach((key) => {
          selectedRowKeys.length > this.state.selectedRowKeys.length
            ? this.state.data.forEach((item) => {
                if (item.id === key.id && key.type !== "FOLDER") {
                  this.selectedFiles.set(item.id, item);
                }
              })
            : (() => {
                const excess = this.state.selectedRowKeys.filter((element) => selectedRowKeys.indexOf(element) === -1);
                this.state.data.forEach((item) => {
                  if (excess[0] === item.id) {
                    this.selectedFiles.delete(item.id);
                  }
                });
              })();
        });

        this.setState({ selectedRowKeys });
      };

      handleAddAttachments = async () => {
        const { selectedRowKeys } = this.state;

        if (!selectedRowKeys.length) {
          message.error(this.props.t("No files were selected"));
        } else {
          try {
            this.setState({ attachLoading: true });
            await this.props.handleAddAttachments(Array.from(this.selectedFiles.values()));
          } catch (error) {
            throw error;
          } finally {
            this.setState({ attachLoading: false });
          }
        }
      };

      handleChange: TableProps["onChange"] = (pagination) => {
        this.setState({ pagination: pagination });
      };

      loadData = async (folderId?: number) => {
        try {
          this.setState({ loading: true });
          const projectId = projectsDataStore.projectId;
          const parsedFolderId =
            folderId && String(folderId).indexOf("F") !== -1 ? Number(folderId.toString().substring(1)) : folderId;
          const url = folderId
            ? API_URL.DOCUMENTS.getDocumentsByFolderId(projectId, parsedFolderId)
            : API_URL.DOCUMENTS.getDocumentsOfRootFolder(projectId);

          const filterParams: { hideForbidden: true; minAccessLevel: API_DOCUMENTS_MINACCESSLEVEL } = {
            hideForbidden: true,
            minAccessLevel: this.props.minimumAccessLevel,
          };

          const defaultSortParams: API_DocumentSorting = { sortBy: "NAME", sortOrder: "ASC" };
          this.breadcrumbsStore.init(parsedFolderId);
          const response: API_MultiDocumentResponse = await API.getData(url, { ...defaultSortParams, ...filterParams });

          let data = [];
          if (response.items.parentId) {
            data.push({ id: "F" + response.items.parentId, name: "...", type: "FOLDER" });
          }
          if (response.items.subfolders) {
            response.items.subfolders = response.items.subfolders.map(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (item) => (item.type === "FOLDER" ? { ...item, id: `F${item.id}` as any } : item)
            );
            data = data.concat(response.items.subfolders);
          }
          if (response.items.files) {
            data = data.concat(response.items.files);
          }
          this.setState({ data, pagination: { ...this.state.pagination, current: 1 } });
        } catch (error) {
          throw error;
        } finally {
          this.setState({ loading: false });
        }
      };

      filterData = (data: ProjectDocumentTableItem[]) => {
        if (this.props.allowedFileTypes) {
          return data?.filter(
            (item) => this.props.allowedFileTypes.includes(item.type) || item.type.startsWith("FOLD")
          );
        }
        return data;
      };

      checkIfNonConverted = (record: API_FileDataEntity) => {
        if (record.xktVersion === false && ["xkt", "ifc"].includes(record.type)) {
          return true;
        }
        return false;
      };

      getRowClassName = (record: API_FileDataEntity) => {
        const { fromXKTViewer } = this.props;
        if (fromXKTViewer) {
          if (this.checkIfNonConverted(record)) {
            return "greyCell";
          }
        }
        return "files-row";
      };

      render() {
        const { t, allowedFileTypes, fromXKTViewer } = this.props;
        const { data, selectedRowKeys, loading } = this.state;
        return (
          <>
            <DocumentsBreadcrumbs goToFolder={this.loadData} config={this.breadcrumbsStore.data} />
            <div className="mb-2 files-table" style={{ fontSize: "12px" }}>
              <Table<API_FileDataEntity>
                name="addFilesDocuments"
                dataSource={this.filterData(data)}
                rowKey="id"
                loading={loading}
                size="small"
                rowClassName={this.getRowClassName}
                onChange={this.handleChange}
                rowSelection={{
                  selectedRowKeys: selectedRowKeys,
                  onChange: this.onSelectChange,
                  getCheckboxProps: (record) => ({
                    disabled:
                      record.type.startsWith("FOLD") ||
                      (fromXKTViewer && !record.xktVersion) ||
                      (allowedFileTypes && !allowedFileTypes.includes(record.type)),
                  }),
                  type: this.props.isRadioSelection ? "radio" : "checkbox",
                }}
                onRow={(record) => ({
                  onClick: () => (record.type === "FOLDER" ? this.loadData(record.id) : undefined),
                  title:
                    fromXKTViewer && this.checkIfNonConverted(record)
                      ? t("File not available: Being processed")
                      : undefined,
                })}
                pagination={this.state.pagination}
              >
                <ProjectFilesColumn
                  dataIndex={"type"}
                  width={40}
                  render={(text, record) => {
                    return text === "FOLDER" ? (
                      <FolderTypeIcon
                        state={(record as API_FolderDataEntity).state}
                        folderAccess={(record as API_FolderDataEntity).folderAccess}
                      />
                    ) : (
                      <FileTypeIcon type={record.type} state={record.state} malware={record.malware} />
                    );
                  }}
                />
                <ProjectFilesColumn title={t("Filename")} dataIndex={"name"} />
                <ProjectFilesColumn
                  title={t("Size")}
                  dataIndex={"size"}
                  render={(text, record) => (record.size ? FormatData.formatBytes(text, 1) : null)}
                />
                <ProjectFilesColumn
                  title={t("Last modified")}
                  dataIndex={"created"}
                  render={(text, record) => (record.created ? dayjs(+record.created).format("lll") : null)}
                />
              </Table>
            </div>
            <ButtonsRow justify="space-between">
              <Button
                hidden={this.props.hideSearch}
                onClick={this.props.onShowSearch}
                type="default"
                data-testid="project-docs-file-search-button"
              >
                <FontAwesomeIcon icon={["far", "search"]} style={{ marginRight: 5 }} fixedWidth={true} size="lg" />
                {t("File search")}
              </Button>
              <Button
                onClick={this.handleAddAttachments}
                loading={this.state.attachLoading}
                type="primary"
                data-testid="project-docs-add-files-button"
              >
                {t(this.props.isRadioSelection ? "Add file" : "Add files")}
              </Button>
            </ButtonsRow>
          </>
        );
      }
    }
  )
);
